// User-facing tracking profile presets. Stored in localStorage so each
// device can opt into a battery profile independently of the org.
import { useEffect, useState } from "react";

export type TrackingProfile = "balanced" | "aggressive" | "battery_saver";

export type TrackingProfileConfig = {
  /** Min ms between accepted geolocation updates */
  minIntervalMs: number;
  /** Min meters traveled to record a new point */
  minDistanceM: number;
  /** Batch size for upload */
  batchSize: number;
  /** Max ms a point sits in the local batch before being flushed */
  flushIntervalMs: number;
  /** Watch options */
  highAccuracy: boolean;
  /** Battery percentage below which we automatically downgrade */
  lowBatteryFloorPct: number;
};

export const TRACKING_PROFILES: Record<TrackingProfile, TrackingProfileConfig> = {
  aggressive: {
    minIntervalMs: 5_000, minDistanceM: 5, batchSize: 12,
    flushIntervalMs: 10_000, highAccuracy: true, lowBatteryFloorPct: 15,
  },
  balanced: {
    minIntervalMs: 15_000, minDistanceM: 10, batchSize: 8,
    flushIntervalMs: 20_000, highAccuracy: true, lowBatteryFloorPct: 20,
  },
  battery_saver: {
    minIntervalMs: 30_000, minDistanceM: 25, batchSize: 4,
    flushIntervalMs: 45_000, highAccuracy: false, lowBatteryFloorPct: 30,
  },
};

const STORAGE_KEY = "mr.trackingProfile";

export function getTrackingProfile(): TrackingProfile {
  if (typeof window === "undefined") return "balanced";
  const v = localStorage.getItem(STORAGE_KEY) as TrackingProfile | null;
  return v && v in TRACKING_PROFILES ? v : "balanced";
}

export function setTrackingProfile(p: TrackingProfile) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, p);
  window.dispatchEvent(new CustomEvent("mr:tracking-profile", { detail: p }));
}

export function useTrackingProfile(): [TrackingProfile, (p: TrackingProfile) => void] {
  const [p, setP] = useState<TrackingProfile>(() => getTrackingProfile());
  useEffect(() => {
    const handler = (e: Event) => setP((e as CustomEvent<TrackingProfile>).detail);
    window.addEventListener("mr:tracking-profile", handler);
    return () => window.removeEventListener("mr:tracking-profile", handler);
  }, []);
  return [p, (next) => { setTrackingProfile(next); setP(next); }];
}
